import React, {useEffect, useRef, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import PayDateFilter from '../../accounting/carrier-pay/carrier-pay-date-filter'
import {getProp} from '../../../common/util/util-helpers'
import {download} from '../../../data/actions/download'
import {currentDate} from '../../../common/util/util-dates'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import Tippy from '@tippyjs/react'
import TableFilters from '../../../common/components/resource-table/table-components/table-filters'
import ActiveFilters from '../../../common/components/resource-table/table-components/active-filters'
import {excludeFields} from '../../../common/util/util-fields'
import moment from 'moment'
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from '../../../common/util/util-consts'
import Chart from 'chart.js/auto'
import {getHeatColor} from '../../../common/util/util-formaters'

const fmt$ = (v) => `$${Number(v ?? 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const getSelects = () => ({
    OfficeID: {
        api: 'api/' + Resources.OfficesQuick,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({value: item.OfficeID, label: item.OfficeName})
    },
})

const WO_REPORTS_DATES_KEY = 'wo_reports_dates'
const defaultStart = () => moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
const defaultEnd = () => moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
const getSavedDates = () => LocalStorage.get(WO_REPORTS_DATES_KEY) || {}

const getQueryFields = () => {
    const saved = getSavedDates()
    return {
    StartDate: new Field('StartDate', saved.StartDate || defaultStart(), [], false, 'date', {}, {}),
    EndDate: new Field('EndDate', saved.EndDate || defaultEnd(), [], false, 'date', {}, {}),
    OfficeID: new Field('OfficeID', '', [''], false, 'select-search', {}, {isClearable: true}),
    NotSoldRetired: new Field('NotSoldRetired', '1', [''], false, 'checkbox', {}),
}}

const BarChart = ({list, miscTotal}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    useEffect(() => {
        if (!canvasRef.current) return
        if (chartRef.current) chartRef.current.destroy()

        const items = [...(list ?? [])]
        if (miscTotal > 0) {
            items.push({CustomCode: 'Misc / No Code', TotalAmount: miscTotal, PartsAmount: 0, LaborAmount: 0})
        }

        const labels = items.map(i => i.CustomCode || (i.CodeNumber ? `Code ${i.CodeNumber}` : 'No Code'))
        const partsData = items.map(i => i.PartsAmount ?? 0)
        const laborData = items.map(i => i.LaborAmount ?? 0)
        const otherData = items.map(i => {
            const other = (i.TotalAmount ?? 0) - (i.PartsAmount ?? 0) - (i.LaborAmount ?? 0)
            return other > 0 ? other : 0
        })

        const ctx = canvasRef.current.getContext('2d')
        chartRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Parts',
                        data: partsData,
                        backgroundColor: '#3b82f655',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                    {
                        label: 'Labor',
                        data: laborData,
                        backgroundColor: '#10b98155',
                        borderColor: '#10b981',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                    {
                        label: 'Other',
                        data: otherData,
                        backgroundColor: '#f59e0b55',
                        borderColor: '#f59e0b',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {font: {size: 11}, boxWidth: 12, padding: 14},
                    },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        titleFont: {weight: '600', size: 13},
                        bodyFont: {size: 12},
                        callbacks: {
                            label: (item) => {
                                const val = item.raw
                                return ` ${item.dataset.label}: ${fmt$(val)}`
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: {display: false},
                        ticks: {
                            color: '#6b7280',
                            font: {size: 10},
                            maxRotation: 45,
                            minRotation: 30,
                        },
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        stacked: true,
                        grid: {color: '#f3f4f6'},
                        ticks: {
                            color: '#6b7280',
                            font: {size: 11},
                            callback: (v) => `$${v.toLocaleString()}`,
                        },
                        border: {display: false},
                        title: {display: true, text: 'Amount ($)', color: '#6b7280', font: {size: 11}},
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [list, miscTotal])

    return <canvas ref={canvasRef}/>
}

export default function CostPerCodeReportTab({translate}) {
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    const isLoading = secondResource.isLoading
    const list = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)
    const miscTotal = getProp(secondResource.data, 'MiscTotal', 0)
    const miscPaidAmount = getProp(secondResource.data, 'MiscPaidAmount', 0)
    const miscChargedAmount = getProp(secondResource.data, 'MiscChargedAmount', 0)
    const grandTotalAmount = getProp(secondResource.data, 'TotalAmount', 0)
    const grandPaidAmount = getProp(secondResource.data, 'PaidAmount', 0)
    const grandChargedAmount = getProp(secondResource.data, 'ChargedAmount', 0)

    const [isTrailerMode, setIsTrailerMode] = useState(false)
    const [queryFields, setQueryFields] = useState(getQueryFields())
    const [sortCol, setSortCol] = useState('TotalAmount')
    const [sortDir, setSortDir] = useState('desc')

    const getQuery = () => {
        const base = FieldsManager.getFieldKeyValues(queryFields)
        if (isTrailerMode) base.IsTrailer = 1
        return base
    }

    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrdersReportsPerCode,
            query: getQuery()
        }))
    }

    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrdersReportsPerCode,
            query: Object.assign({
                format: 'EXCEL',
                name: 'cost_per_code_report_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    useEffect(() => {
        fetchData()
    }, [queryFields, isTrailerMode])

    const handleQueryChange = (name, value) => {
        let fields = FieldsManager.updateField(queryFields, name, value)
        if (name === 'StartDate') fields.EndDate.props.minDate = value
        if (name === 'EndDate') fields.StartDate.props.maxDate = value
        setQueryFields(fields)

        if (name === 'StartDate' || name === 'EndDate') {
            LocalStorage.set(WO_REPORTS_DATES_KEY, {...getSavedDates(), [name]: value})
        }
    }

    const handleClearFiltersClick = () => {
        const fieldsTmp = Object.assign({}, queryFields)
        Object.values(fieldsTmp).forEach((it) => {
            FieldsManager.updateField(fieldsTmp, it.name, '')
        })
        setQueryFields(fieldsTmp)
    }

    const handleModeSwitch = (trailer) => {
        if (trailer === isTrailerMode) return
        setIsTrailerMode(trailer)
        setQueryFields(getQueryFields())
    }

    const handleSortChange = (col) => {
        if (col === sortCol) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortCol(col)
            setSortDir('desc')
        }
    }

    const SortIcon = ({col}) => {
        if (sortCol !== col) return <span className="ml-1 text-tm-gray-300">↕</span>
        return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
    }

    const getDateFilterLabel = () => {
        const {StartDate, EndDate} = queryFields
        if (!StartDate.value || !EndDate.value) return translate('text.select_date_range') || 'Select dates'
        const fmt = (d) => new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
        return `${fmt(StartDate.value)} – ${fmt(EndDate.value)}`
    }

    const maxTotal = Math.max(...list.map(r => r.TotalAmount ?? 0), 0)
    const minTotal = Math.min(...list.map(r => r.TotalAmount ?? 0), 0)
    const maxPaid = Math.max(...list.map(r => r.PaidAmount ?? 0), 0)
    const minPaid = Math.min(...list.map(r => r.PaidAmount ?? 0), 0)

    const sortedList = [...list].sort((a, b) => {
        const av = a[sortCol] ?? ''
        const bv = b[sortCol] ?? ''
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
        return sortDir === 'asc' ? cmp : -cmp
    })

    const hasData = list.length > 0 || miscTotal > 0

    return (
        <div>
            <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card">
                <div className="flex items-center justify-between px-4 pt-2">
                    <span className="text-xs text-tm-gray-400">{getDateFilterLabel()}</span>

                    <div className="flex items-center rounded-lg border border-tm-gray-300 overflow-hidden text-sm font-medium">
                        <button
                            className={`px-3 py-1 transition-colors ${!isTrailerMode ? 'bg-primary text-white' : 'text-tm-gray-600 hover:bg-tm-gray-100'}`}
                            onClick={() => handleModeSwitch(false)}
                        >
                            Trucks
                        </button>
                        <button
                            className={`px-3 py-1 transition-colors ${isTrailerMode ? 'bg-primary text-white' : 'text-tm-gray-600 hover:bg-tm-gray-100'}`}
                            onClick={() => handleModeSwitch(true)}
                        >
                            Trailers
                        </button>
                    </div>
                </div>

                <div className="sm:flex justify-between items-start">
                    <ActiveFilters
                        addClass="px-4 pt-2"
                        filterFields={queryFields}
                        onLabelClick={handleQueryChange}
                        persistFilters={['StartDate', 'EndDate']}
                        onClearFiltersClick={handleClearFiltersClick}
                        translate={translate}
                    />

                    <div className="px-4 pt-2 flex-shrink-0">
                        <Tippy content={translate('text.download_excel')}>
                            <button className="btn-icon" onClick={downloadExcel}>
                                <DocumentArrowDownIcon className="w-5 h-5"/>
                            </button>
                        </Tippy>
                    </div>
                </div>

                <TableFilters
                    forceDialog
                    hideLimit
                    filterFields={excludeFields(queryFields, ['StartDate', 'EndDate'])}
                    handleInputChange={handleQueryChange}
                    translate={translate}
                    onRefreshTable={fetchData}
                    selects={getSelects()}
                    isLoading={isLoading}
                    customHtml={
                        <PayDateFilter
                            queryFields={queryFields}
                            hasDateType={false}
                            translate={translate}
                            updateQueryFields={(qf) => {
                                setQueryFields(qf)
                                LocalStorage.set(WO_REPORTS_DATES_KEY, {
                                    StartDate: qf.StartDate?.value,
                                    EndDate: qf.EndDate?.value,
                                })
                            }}
                            onQueryChange={handleQueryChange}
                        />
                    }
                />
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"/>
                </div>
            )}

            {!isLoading && hasData && (
                <div className="space-y-6 py-4">
                    {/* Bar Chart */}
                    <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card">
                        <div className="px-4 pt-3 pb-1">
                            <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide">
                                Cost by Code
                            </p>
                        </div>
                        <div className="p-4" style={{height: 340}}>
                            <BarChart list={list} miscTotal={miscTotal}/>
                        </div>
                    </div>

                    {/* Grand Totals Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {[
                            {label: 'Total Amount', value: grandTotalAmount, color: 'text-blue-600'},
                            {label: 'Paid Amount', value: grandPaidAmount, color: 'text-green-600'},
                            {label: 'Charged Amount', value: grandChargedAmount, color: 'text-tm-gray-700'},
                        ].map(({label, value, color}) => (
                            <div key={label}
                                 className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card p-4">
                                <p className="text-xs text-tm-gray-400 mb-1">{label}</p>
                                <p className={`text-lg font-semibold tabular-nums ${color}`}>{fmt$(value)}</p>
                            </div>
                        ))}
                    </div>

                    {/* Code Table */}
                    <div>
                        <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2 px-1">
                            By Code ({count}{miscTotal > 0 ? ' + Misc' : ''})
                        </p>
                        <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card overflow-auto">
                            <table className="min-w-full text-sm divide-y divide-tm-gray-300">
                                <thead className="bg-tm-gray-50">
                                <tr>
                                    {[
                                        {col: 'CodeNumber', label: '#', align: 'left'},
                                        {col: 'CustomCode', label: 'Code', align: 'left'},
                                        {col: 'PartsAmount', label: 'Parts', align: 'right'},
                                        {col: 'LaborAmount', label: 'Labor', align: 'right'},
                                        {col: 'TotalAmount', label: 'Total', align: 'right'},
                                        {col: 'PaidAmount', label: 'Paid', align: 'right'},
                                        {col: 'ChargedAmount', label: 'Charged', align: 'right'},
                                    ].map(({col, label, align}) => (
                                        <th
                                            key={col}
                                            className={`px-4 py-2.5 text-${align} font-medium text-tm-gray-700 whitespace-nowrap cursor-pointer select-none hover:bg-tm-gray-100`}
                                            onClick={() => handleSortChange(col)}
                                        >
                                            {label}<SortIcon col={col}/>
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-tm-gray-200">
                                {sortedList.map((row, i) => (
                                    <tr key={row.CustomCodeID ?? `no-code-${i}`} className="bg-popup hover:bg-tm-gray-50 transition-colors">
                                        <td className="px-4 py-2.5 text-tm-gray-500 text-xs">{row.CodeNumber || '—'}</td>
                                        <td className="px-4 py-2.5 font-medium text-tm-gray-800">{row.CustomCode || 'No Code'}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-600 text-xs">{fmt$(row.PartsAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-600 text-xs">{fmt$(row.LaborAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold" style={{color: getHeatColor(row.TotalAmount ?? 0, minTotal, maxTotal)}}>{fmt$(row.TotalAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs" style={{color: getHeatColor(row.PaidAmount ?? 0, minPaid, maxPaid)}}>{fmt$(row.PaidAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs text-tm-gray-600">{fmt$(row.ChargedAmount)}</td>
                                    </tr>
                                ))}

                                {miscTotal > 0 && (
                                    <tr className="bg-popup hover:bg-tm-gray-50 transition-colors italic">
                                        <td className="px-4 py-2.5 text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-tm-gray-500">Misc / No Code</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold" style={{color: getHeatColor(miscTotal, minTotal, maxTotal)}}>{fmt$(miscTotal)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs" style={{color: getHeatColor(miscPaidAmount, minPaid, maxPaid)}}>{fmt$(miscPaidAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs text-tm-gray-600">{fmt$(miscChargedAmount)}</td>
                                    </tr>
                                )}
                                </tbody>
                                <tfoot>
                                <tr className="bg-tm-gray-50 font-semibold border-t-2 border-tm-gray-300">
                                    <td colSpan={2} className="px-4 py-2.5 text-tm-gray-700">Grand Total</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">
                                        {fmt$(sortedList.reduce((s, r) => s + (r.PartsAmount ?? 0), 0))}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">
                                        {fmt$(sortedList.reduce((s, r) => s + (r.LaborAmount ?? 0), 0))}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandTotalAmount)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandPaidAmount)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandChargedAmount)}</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && !hasData && (
                <div className="flex items-center justify-center py-20 text-sm text-tm-gray-400">
                    No data for the selected period
                </div>
            )}
        </div>
    )
}
